import { Component, OnInit } from '@angular/core';
import { first } from 'rxjs/operators';
import { LogmanService, Logman } from '@app/_components/logman';
import { AlertService, AuthenticationService, DataCacheService } from '@app/_services';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectItem, LazyLoadEvent, MessageService } from 'primeng/api';
import { Locations, ListingStatus } from '@app/_helpers/constants';
import { DialogService } from 'primeng/dynamicdialog';
import { LogmanComponent } from '../logman/logman.component';


@Component({
    selector: 'app-logman-list',
    templateUrl: './logman-list.component.html',
    styleUrls: ['./logman-list.component.css'],
    providers: [DialogService],
    standalone: false
})
export class LogmanListComponent implements OnInit {
	datasource: Logman[];
	logman: Logman;
	logmans: Logman[] = [];
	columns: any[];
	recordTotal: number;
	lazyLoadEvent: LazyLoadEvent;
	loading = true;
	userStatusOptions: SelectItem[];
	interval;
	referenceId: any;
	severity: any;
	reservationId: any;
	shortTermRentalId: any;
	referenceIdOptions: SelectItem[] = [];
	selectedReferenceIds: any[] = [];
	shortTermRentalIdOptions: SelectItem[] = [];
	selectedShortTermRentalIds: any[] = [];

	severities = [
		{ name: 'Error', value: 'ERROR' },
		{ name: 'Info', value: 'INFO' },
		{ name: 'Debug', value: 'DEBUG' },
		{ name: 'Report', value: 'REPORT' },
		{ name: 'Success', value: 'SUCCESS' },
	];



	constructor(
		private authenticationService: AuthenticationService,
		private logmanService: LogmanService,
		private dataCacheService: DataCacheService,
		private alertService: AlertService,
		private messageService: MessageService,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		public dialogService: DialogService
	) { }


	ngOnInit() {
		this.activatedRoute.params.subscribe(params => {
			this.referenceId = params.referenceId;
			this.severity = params.severity;
			this.reservationId = params.reservationId;
			this.shortTermRentalId = params.shortTermRentalId;

			if (this.shortTermRentalId != null) {
				this.selectedShortTermRentalIds = [String(this.shortTermRentalId)];
			}

			// If the route param is numeric, try fetching by logmanId first.
			// If found, open detail dialog. If not found, fall back to referenceId search.
			if (this.referenceId != null && /^\d+$/.test(String(this.referenceId))) {
				this.logmanService.findById(Number(this.referenceId)).pipe(first()).subscribe(
					res => {
						if (res.data && res.data.logman) {
							// Found by logmanId — open the detail dialog
							this.openLogmanById(Number(this.referenceId));
						} else {
							// Not found by logmanId — fall back to referenceId search
							this.selectedReferenceIds = [String(this.referenceId)];
						}
						this.loadLazy(this.lazyLoadEvent);
					},
					err => {
						// findById failed (404 etc.) — fall back to referenceId search
						this.selectedReferenceIds = [String(this.referenceId)];
						this.loadLazy(this.lazyLoadEvent);
					}
				);
			} else {
				if (this.referenceId != null) {
					this.selectedReferenceIds = [String(this.referenceId)];
				}
				this.loadLazy(this.lazyLoadEvent);
			}
		});
		this.loadDistinctReferenceIds();
		this.loadDistinctShortTermRentalIds();
	}

	loadDistinctReferenceIds() {
		this.logmanService.findDistinctReferenceIds().pipe(first()).subscribe(
			res => {
				// Handle response structure: { statusCode, message, referenceIds: [...] }
				// According to endpoint docs, referenceIds is at root level
				// But also check data.referenceIds for compatibility
				const referenceIds = (res as any).referenceIds || (res.data && res.data.referenceIds) || [];
				if (referenceIds && referenceIds.length > 0) {
					// Convert array of referenceIds to SelectItem format
					// Backend already filters out null/empty and sorts alphabetically
					this.referenceIdOptions = referenceIds
						.filter((id: any) => id != null && id !== '') // Additional safety filter
						.map((id: any) => ({ label: String(id), value: String(id) }))
						.sort((a: SelectItem, b: SelectItem) => {
							// Sort numerically if possible, otherwise alphabetically
							// (Backend already sorts alphabetically, but we enhance with numeric sorting)
							const numA = Number(a.value);
							const numB = Number(b.value);
							if (!isNaN(numA) && !isNaN(numB)) {
								return numA - numB;
							}
							return String(a.value).localeCompare(String(b.value));
						});
				}
			},
			err => {
				console.log('Error loading distinct referenceIds: ' + JSON.stringify(err));
				this.alertService.error(err);
			}
		);
	}

	onReferenceIdFilterChange(event: any) {
		// Update the selected values
		this.selectedReferenceIds = event.value || [];
		
		// Create a fresh event object to ensure filters are properly reset
		// Copy existing filters but exclude referenceId (we'll handle it separately)
		const existingFilters = this.lazyLoadEvent?.filters || {};
		const filtersWithoutReferenceId: any = {};
		Object.keys(existingFilters).forEach(key => {
			if (key !== 'referenceId') {
				filtersWithoutReferenceId[key] = existingFilters[key];
			}
		});
		
		const filterEvent: LazyLoadEvent = {
			first: 0, // Reset to first page when filter changes
			rows: this.lazyLoadEvent?.rows || 250,
			sortField: this.lazyLoadEvent?.sortField || null,
			sortOrder: this.lazyLoadEvent?.sortOrder || null,
			filters: filtersWithoutReferenceId,
			globalFilter: this.lazyLoadEvent?.globalFilter || null,
			multiSortMeta: this.lazyLoadEvent?.multiSortMeta || null
		};
		
		// Trigger table reload with updated filter
		this.loadLazy(filterEvent);
	}

	loadDistinctShortTermRentalIds() {
		this.logmanService.findDistinctShortTermRentalIds().pipe(first()).subscribe(
			res => {
				const shortTermRentalIds = (res as any).shortTermRentalIds || (res.data && res.data.shortTermRentalIds) || [];
				if (shortTermRentalIds && shortTermRentalIds.length > 0) {
					this.shortTermRentalIdOptions = shortTermRentalIds
						.filter((id: any) => id != null && id !== '')
						.map((id: any) => ({ label: String(id), value: String(id) }))
						.sort((a: SelectItem, b: SelectItem) => Number(a.value) - Number(b.value));
				}
			},
			err => {
				console.log('Error loading distinct shortTermRentalIds: ' + JSON.stringify(err));
				this.alertService.error(err);
			}
		);
	}

	onShortTermRentalIdFilterChange(event: any) {
		this.selectedShortTermRentalIds = event.value || [];

		const existingFilters = this.lazyLoadEvent?.filters || {};
		const filtersWithoutShortTermRentalId: any = {};
		Object.keys(existingFilters).forEach(key => {
			if (key !== 'shortTermRentalId') {
				filtersWithoutShortTermRentalId[key] = existingFilters[key];
			}
		});

		const filterEvent: LazyLoadEvent = {
			first: 0,
			rows: this.lazyLoadEvent?.rows || 250,
			sortField: this.lazyLoadEvent?.sortField || null,
			sortOrder: this.lazyLoadEvent?.sortOrder || null,
			filters: filtersWithoutShortTermRentalId,
			globalFilter: this.lazyLoadEvent?.globalFilter || null,
			multiSortMeta: this.lazyLoadEvent?.multiSortMeta || null
		};

		this.loadLazy(filterEvent);
	}



	loadLazy(event: LazyLoadEvent) {
		try {
			this.loading = true;
			// Initialize event if null/undefined
			if (!event) {
				event = {
					first: 0,
					rows: 250,
					sortField: null,
					sortOrder: null,
					filters: {},
					globalFilter: null,
					multiSortMeta: null
				} as LazyLoadEvent;
			}
			this.lazyLoadEvent = event;

			console.log(
				' event.first:' + event.first
				+ '  |  event.rows:' + event.rows
				+ '  |  event.sortField:' + event.sortField
				+ '  |  event.sortOrder:' + event.sortOrder
				+ '  |  event.multiSortMeta:' + JSON.stringify(event.multiSortMeta)
				+ '  |  event.globalFilter:' + event.globalFilter
				+ '  |  event.filters:' + JSON.stringify(event.filters)
			);

			// Initialize filters if not present
			if (!event.filters) {
				event.filters = {};
			}

			/*
			*
			* Default Sort
			*/
			// https://stackoverflow.com/questions/679915/how-do-i-test-for-an-empty-javascript-object
			// if we are NOT filtering and we are NOT sorting, apply a default sort
			if (Object.keys(event.filters).length === 0 && !event.multiSortMeta) {
				event.multiSortMeta = [];
				event.multiSortMeta.push({ field: 'createDate', order: -1 });
			}

			// Handle referenceId filter
			// Route params are already handled in ngOnInit by setting selectedReferenceIds
			if (this.selectedReferenceIds && this.selectedReferenceIds.length > 0) {
				// Apply multiselect filter
				event.filters['referenceId'] = { "value": this.selectedReferenceIds, "matchMode": "in" };
			} else {
				// Clear the filter when multiselect is empty/cleared
				if (event.filters && event.filters['referenceId']) {
					delete event.filters['referenceId'];
				}
			}
			if (this.selectedShortTermRentalIds && this.selectedShortTermRentalIds.length > 0) {
				event.filters['shortTermRentalId'] = { "value": this.selectedShortTermRentalIds, "matchMode": "in" };
			} else {
				if (event.filters && event.filters['shortTermRentalId']) {
					delete event.filters['shortTermRentalId'];
				}
			}
			if (this.severity != null) {
				event.filters['severity'] = { "value": this.severity, "matchMode": "in" };
			}
			if (this.reservationId != null) {
				event.filters['reservationId'] = { "value": this.reservationId, "matchMode": "in" };
			}
			const pageNumber = Math.round(event.first / event.rows);
			this.logmanService.findByFilter(
				pageNumber, event.rows,
				event.sortField,
				event.sortOrder,
				event.multiSortMeta,
				event.globalFilter,
				event.filters).pipe(first()).subscribe(

					res => {
						this.datasource = res.data.logmans;
						this.recordTotal = res.page.recordTotal;
						this.logmans = res.data.logmans;
						console.log(JSON.stringify(res.page));
					},
					err => {
						console.log('logmans list error!: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.loading = false; }
				);

			if (this.datasource) {
				this.logmans = this.datasource.slice(event.first, (event.first + event.rows));
			}
		} catch (error) {
			console.log('loadLazy(' + event + ')' + error);
		}
	}
	openLogmanById(logmanId: number) {
		const ref = this.dialogService.open(LogmanComponent, {
			data: {
				logman: { logmanId: logmanId }
			},
			header: 'Logman #' + logmanId,
			width: '80%',
			closeOnEscape: true
		});

		ref.onClose.subscribe((logman: Logman) => {
			if (logman) {
				if (logman.action === 'DELETED') {
					this.messageService.add({ severity: 'info', summary: 'Success', detail: 'Logman ' + logman.logmanId + ' Deleted.', sticky: false });
				}
			}
		});
	}

	onRowSelect(event) {

		const ref = this.dialogService.open(LogmanComponent, {
			data: {
				logman: event.data
			},
			header: 'Logman',
			width: '80%',
			closeOnEscape: true
		});

		ref.onClose.subscribe((logman: Logman) => {
			if (logman) {
				if (logman.action === 'UPDATED') {
					var foundIndex = this.logmans.findIndex(x => x.logmanId == logman.logmanId);
					this.logmans[foundIndex] = logman;
				}

				if (logman.action === 'DELETED') {
					var foundIndex = this.logmans.findIndex(x => x.logmanId == logman.logmanId);
					this.logmans.splice(foundIndex, 1);
					this.messageService.add({ severity: 'info', summary: 'Success', detail: 'Logman ' + logman.logmanId + ' Deleted.', sticky: false });
				}
			}
		});
	}



	onNewLogmanButtonClick() {
		const ref = this.dialogService.open(LogmanComponent, {
			header: 'Logman',
			width: '80%',
			closeOnEscape: true
		});

		ref.onClose.subscribe((logman: Logman) => {
			if (logman) {

				if (logman.action === 'ADDED') {
					this.logmans.unshift(logman);
				}
			}
		});
	}
	get Severities() {
		return this.severities;
	}

	getSeverityName(logman: any) {
		const severity = this.severities.find(e => e.value === logman.severity);
		return severity.name;
	}
}
